/*
 * PDP desktop (990px+) behaviour for the House of Spoils layout.
 * Wraps the existing product-info / artwork-viewer behaviour — it never touches variant or add-to-cart logic.
 *  - prev/next arrows + wrap-around for the hero/builder media carousel (the counter is updated by product-info.js's scroll listener)
 *  - "Select Size, Frame & Mount" expands the collapsed options, scrolls them into view and jumps to the builder slide
 *  - any option change (size, frame, mount, colour/B&W) also lands on the builder slide (desktop twin of the mobile snap)
 *  - deep links (?variant= / ?option_values=) auto-expand the options when a non-default variant is selected
 *  - "Read more" expander for the short description
 * All listeners are delegated from <product-info>, so they survive the section re-render on variant change.
 */
(() => {
  const desktopQuery = window.matchMedia('(min-width: 990px)');
  const reduceMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const isDesktop = () => desktopQuery.matches;

  const initRoot = (root) => {
    if (!root || root.dataset.pdpDesktopReady === '1') return;
    root.dataset.pdpDesktopReady = '1';

    const getShowcase = () => root.querySelector('.product-media-showcase--has-hero-slide');
    const getTrack = () => getShowcase()?.querySelector('.product-media-showcase__track') || null;
    const getDetails = () => root.querySelector('.product-variant-details');
    const slideCount = () => getShowcase()?.querySelectorAll('.product-media-showcase__slide').length || 0;

    const currentIndex = () => {
      const track = getTrack();
      if (!track) return 0;
      const width = track.clientWidth || 1;
      return Math.max(0, Math.min(slideCount() - 1, Math.round(track.scrollLeft / width)));
    };

    const goToSlide = (index) => {
      const track = getTrack();
      const count = slideCount();
      if (!track || count < 2) return;
      const target = ((index % count) + count) % count;
      const width = track.clientWidth || 1;
      track.scrollTo({ left: target * width, behavior: reduceMotionQuery.matches ? 'auto' : 'smooth' });
    };

    /* Two frames so the artwork viewer has applied the new option before the slide moves. */
    let pendingSnap = null;
    const snapToBuilder = () => {
      if (!isDesktop()) return;
      if (pendingSnap) cancelAnimationFrame(pendingSnap);
      pendingSnap = requestAnimationFrame(() => {
        pendingSnap = requestAnimationFrame(() => {
          pendingSnap = null;
          if (!isDesktop()) return;
          goToSlide(1);
        });
      });
    };

    const scrollDetailsIntoView = (details) => {
      if (!details || !isDesktop()) return;
      const margin = parseFloat(getComputedStyle(details).scrollMarginTop) || 32;
      const top = details.getBoundingClientRect().top + window.scrollY - margin;
      window.scrollTo({ top: Math.max(0, top), behavior: reduceMotionQuery.matches ? 'auto' : 'smooth' });
    };

    const openDetails = (details, { scroll = true, snap = true } = {}) => {
      if (!details) return;
      if (!details.open) details.setAttribute('open', '');
      if (snap) snapToBuilder();
      if (scroll) {
        /* Let the max-height transition start before measuring the target position. */
        requestAnimationFrame(() => scrollDetailsIntoView(details));
      }
    };

    /* ---------- Delegated clicks: arrows, summary, read more ---------- */
    root.addEventListener('click', (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;

      const arrow = target.closest('[data-pdp-media-arrow]');
      if (arrow && root.contains(arrow)) {
        event.preventDefault();
        goToSlide(currentIndex() + (arrow.dataset.pdpMediaArrow === 'prev' ? -1 : 1));
        return;
      }

      const descToggle = target.closest('[data-pdp-desc-toggle]');
      if (descToggle && root.contains(descToggle)) {
        event.preventDefault();
        const wrap = descToggle.closest('[data-pdp-desc]');
        const expanded = wrap?.classList.toggle('is-expanded');
        descToggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        return;
      }

      const summary = target.closest('.product-variant-details__summary');
      if (summary && root.contains(summary) && isDesktop()) {
        const details = summary.closest('.product-variant-details');
        /* Click fires before the native toggle, so `open` is still the previous state. */
        if (details && !details.open) {
          snapToBuilder();
          requestAnimationFrame(() => requestAnimationFrame(() => scrollDetailsIntoView(details)));
        }
      }
    });

    /* Keyboard: arrows on the media column */
    getShowcase()?.addEventListener('keydown', (event) => {
      if (!isDesktop()) return;
      if (event.key === 'ArrowLeft') goToSlide(currentIndex() - 1);
      else if (event.key === 'ArrowRight') goToSlide(currentIndex() + 1);
      else return;
      event.preventDefault();
    });

    /* ---------- Option changes land on the builder slide (mirrors product-info.js's mobile snap) ---------- */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (msg) => {
        const sourceEvent = msg?.data?.event;
        if (!sourceEvent?.isTrusted && !sourceEvent?.__hosFromVariantLabelTap) return;
        const t = sourceEvent?.target;
        if (!t || !root.contains(t)) return;
        snapToBuilder();
      });
    }
    root.addEventListener(
      'change',
      (event) => {
        if (!event?.isTrusted && !event?.__hosFromVariantLabelTap) return;
        const t = event.target;
        if (!(t instanceof Element)) return;
        if (t.matches('input[data-mount-option-input]') || t.matches('input[data-artwork-bw-fallback-input]')) {
          snapToBuilder();
        }
      },
      true
    );

    /* ---------- Read more: only show the toggle when the description is actually clamped ---------- */
    const measureDescription = () => {
      root.querySelectorAll('[data-pdp-desc]').forEach((wrap) => {
        const body = wrap.querySelector('[data-pdp-desc-body]');
        const toggle = wrap.querySelector('[data-pdp-desc-toggle]');
        if (!body || !toggle) return;
        if (!isDesktop()) {
          toggle.hidden = true;
          return;
        }
        if (wrap.classList.contains('is-expanded')) {
          toggle.hidden = false;
          return;
        }
        toggle.hidden = body.scrollHeight <= body.clientHeight + 1;
      });
    };

    /* ---------- Deep links: expand when a non-default variant is preselected ---------- */
    const autoExpandForDeepLink = () => {
      if (!isDesktop()) return;
      const details = getDetails();
      if (!details || details.open) return;
      let params;
      try {
        params = new URLSearchParams(window.location.search);
      } catch (e) {
        return;
      }
      const content = root.querySelector('.product-main-content');
      const defaultVariant = content?.dataset.pdpDefaultVariant || '';
      const variantParam = params.get('variant');
      const hasOptionValues = params.has('option_values');
      if ((variantParam && variantParam !== defaultVariant) || hasOptionValues) {
        /* Deep link / reload with ?variant=: show the options, but stay on the room slide —
           the carousel only moves to the builder when the shopper actively picks an option. */
        openDetails(details, { scroll: false, snap: false });
      }
    };

    let resizeFrame = null;
    window.addEventListener('resize', () => {
      if (resizeFrame) cancelAnimationFrame(resizeFrame);
      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        measureDescription();
        /* Keep the current slide aligned after the column width changes. */
        const track = getTrack();
        if (track && isDesktop() && slideCount() > 1) {
          track.scrollTo({ left: currentIndex() * (track.clientWidth || 1), behavior: 'auto' });
        }
      });
    });

    const boot = () => {
      measureDescription();
      autoExpandForDeepLink();
    };
    if (document.readyState === 'complete') boot();
    else window.addEventListener('load', boot, { once: true });
    /* Web fonts can change the clamp; re-measure once they settle. */
    document.fonts?.ready?.then(measureDescription).catch(() => {});
  };

  const bootAll = () => document.querySelectorAll('product-info.pdp-desktop-hos').forEach(initRoot);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bootAll, { once: true });
  else bootAll();

  /* Theme editor: sections re-render in place. */
  document.addEventListener('shopify:section:load', (event) => {
    event.target?.querySelectorAll?.('product-info.pdp-desktop-hos').forEach(initRoot);
  });
})();
